import './styles.css';
import './robots.txt';

/* Animated background
 * Elements:
 *  - Sky gradient
 *  - Colored clouds (multi-lane) + cirrus ribbons
 *  - Single, lowered hill (left) instead of two
 *  - Colorful foreground flowers with sway
 * Timing aims to mirror the Swift implementation (use time/18 scalars)
 */
(function () {
  const canvas = document.getElementById('bg-canvas');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');

  // Device pixel ratio & canvas sizing
  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  let w = 0, h = 0;
  function resize() {
    const rect = canvas.getBoundingClientRect();
    w = Math.max(1, Math.floor(rect.width));
    h = Math.max(1, Math.floor(rect.height));
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
  }
  window.addEventListener('resize', () => { resize(); seedClouds(); seedFlowers(); seedGrass(); }, { passive: true });
  resize();

  // Utilities ---------------------------------------------------------------
  const clamp = (v, a, b) => Math.min(Math.max(v, a), b);
  const mix = (a, b, t) => a + (b - a) * clamp(t, 0, 1);
  function hsvToRgb(h, s, v) {
    const i = Math.floor(h * 6); const f = h * 6 - i;
    const p = v * (1 - s); const q = v * (1 - f * s); const t = v * (1 - (1 - f) * s);
    const m = i % 6;
    const r = [v, q, p, p, t, v][m];
    const g = [t, v, v, q, p, p][m];
    const b = [p, p, t, v, v, q][m];
    return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
  }
  function rgbToStr({ r, g, b }, a = 1) { return `rgba(${r},${g},${b},${a})`; }
  function pastelTintForCloud(seed) {
    const hue = ((seed * 37) % 360) / 360; // match Swift hue mapping
    const base = hsvToRgb(hue, 0.06, 1.0);
    // blend lightly toward white
    const white = { r: 255, g: 255, b: 255 };
    return {
      r: Math.round(mix(base.r, white.r, 0.12)),
      g: Math.round(mix(base.g, white.g, 0.12)),
      b: Math.round(mix(base.b, white.b, 0.12))
    };
  }
  function pastelFlowerColor(seed) {
    const hue = ((seed * 53) % 360) / 360;
    const base = hsvToRgb(hue, 0.55, 1.0);
    const white = { r: 255, g: 255, b: 255 };
    return {
      r: Math.round(mix(base.r, white.r, 0.12)),
      g: Math.round(mix(base.g, white.g, 0.12)),
      b: Math.round(mix(base.b, white.b, 0.12))
    };
  }
  function pr(seed) {
    // Deterministic pseudo-random in [0,1)
    let x = (seed * 9301 + 49297) % 233280;
    return (x / 233280);
  }
  function halton(n, base) {
    let f = 1, r = 0, i = n;
    while (i > 0) { f /= base; r += f * (i % base); i = Math.floor(i / base); }
    return r;
  }

  // Sky ---------------------------------------------------------------------
  function drawSky() {
    const g = ctx.createLinearGradient(0, 0, 0, h);
    // Swift: [Color(0.70,0.90,0.99), Color(0.88,0.96,0.99)]
    g.addColorStop(0, 'rgb(225,244,252)'); // sky near top (approx)
    g.addColorStop(1, 'rgb(179,230,252)'); // near horizon (approx)
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  }

  // Clouds (lanes) ----------------------------------------------------------
  const cloudCount = 8;
  const lanes = [0.22, 0.32, 0.58, 0.70];
  const laneSpeed = [0.88, 1.00, 1.12, 0.95];
  const cloudsSpeedScale = 0.40;
  const clouds = [];
  function seedClouds() {
    clouds.length = 0;
    for (let i = 0; i < cloudCount; i++) {
      clouds.push({ i, lane: i % lanes.length, sizeIndex: i % 8, phase: (i * 0.173) % 1, seed: 100 + i });
    }
  }
  seedClouds();

  function drawLaneClouds(timeSec) {
    const baseR = Math.min(Math.min(w, h) / 10.0, 130.0);
    for (let c of clouds) {
      const lane = c.lane;
      const speed = laneSpeed[lane] * cloudsSpeedScale;
      const laneY = h * [0.22, 0.32, 0.58, 0.70][lane];
      const sizeFactor = [0.50, 0.65, 0.75, 0.95, 1.15, 1.35, 1.55, 1.75][c.sizeIndex];
      const r = baseR * sizeFactor;
      const bobAmp = 6.0 + (c.i % 3) * 2.0;
      const phase = c.phase;
      const frac = (phase + timeSec * (speed / 18.0)) % 1;
      const bob = Math.sin(2 * Math.PI * (phase + timeSec / 18.0)) * bobAmp;

      const spanPad = r * 2.2;
      const span = w + spanPad * 2.0;
      const baseX = -spanPad + frac * span;
      const baseY = laneY + bob;

      // shape selection like Swift switch
      const shapes = [
        { offs: [[-0.70,0.10],[-0.25,-0.22],[0.20,-0.28],[0.70,0.04],[0.05,0.26]], radii: [0.90,0.78,0.82,0.74,0.62] },
        { offs: [[-0.95,0.05],[-0.45,-0.12],[0.10,-0.05],[0.55,0.02],[0.05,0.22]], radii: [0.80,0.70,0.66,0.58,0.52] },
        { offs: [[-0.45,0.18],[0.00,-0.34],[0.42,0.12],[-0.05,0.40]], radii: [0.88,0.82,0.76,0.60] },
        { offs: [[-0.35,0.00],[0.00,-0.22],[0.36,0.02],[0.05,0.26]], radii: [0.78,0.74,0.70,0.58] }
      ];
      const shape = c.i % 4;
      const tint = pastelTintForCloud(c.i);
      for (let pi = 0; pi < shapes[shape].offs.length; pi++) {
        // slight skew like Swift
        const puffR = r * shapes[shape].radii[Math.min(pi, shapes[shape].radii.length - 1)];
        const skewX = (((c.i * 13 + pi * 5) % 5) - 2) * 0.06 * r;
        const skewY = (((c.i * 7 + pi * 3) % 5) - 2) * 0.04 * r;
        const cx = baseX + shapes[shape].offs[pi][0] * r + skewX;
        const cy = baseY + shapes[shape].offs[pi][1] * r + skewY;

        // body gradient stops identical ordering
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, puffR);
        grad.addColorStop(0.0, rgbToStr(tint, 0.98));
        grad.addColorStop(0.80, rgbToStr(tint, 0.98));
        grad.addColorStop(0.92, rgbToStr(tint, 0.10));
        grad.addColorStop(1.0, 'rgba(255,255,255,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(cx, cy, puffR, puffR, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Extra top clouds (lighter)
    const topCount = 3;
    const topHeights = [0.12, 0.20, 0.30];
    for (let t = 0; t < topCount; t++) {
      const laneY = h * topHeights[t];
      const speed = (0.90 + t * 0.08) * cloudsSpeedScale;
      const phase = (t * 0.41) % 1;
      const frac = (phase + timeSec * (speed / 18.0)) % 1;
      const sizeFactor = 0.60 + t * 0.12;
      const jitter = 1.0 + ((t * 19) % 5) * 0.02;
      const r = Math.min(Math.min(w, h) / 10.0, 130.0) * sizeFactor * jitter;
      const bobAmp = 4.0 + t * 1.5;
      const bob = Math.sin(2 * Math.PI * (phase + timeSec / 18.0)) * bobAmp;

      const spanPad = r * 2.2;
      const span = w + spanPad * 2.0;
      const baseX = -spanPad + frac * span;
      const baseY = laneY + bob;

      const puffs = [[-0.70,0.10],[-0.25,-0.22],[0.20,-0.28],[0.70,0.04],[0.05,0.26]];
      const radii = [0.90,0.78,0.82,0.74,0.62].map(v => v * r);
      const tint = pastelTintForCloud(200 + t);
      for (let i = 0; i < puffs.length; i++) {
        const cx = baseX + puffs[i][0] * r;
        const cy = baseY + puffs[i][1] * r;
        const puffR = radii[i];
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, puffR);
        grad.addColorStop(0.0, rgbToStr(tint, 0.98));
        grad.addColorStop(0.82, rgbToStr(tint, 0.98));
        grad.addColorStop(0.92, rgbToStr(tint, 0.10));
        grad.addColorStop(1.0, 'rgba(255,255,255,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(cx, cy, puffR, puffR, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  // Cirrus ribbons ----------------------------------------------------------
  function drawCirrus(timeSec) {
    const drawRibbonSet = (count, baseSpeed, oyFn, thickFn, lenFn, seedBase, alphaBase) => {
      for (let c = 0; c < count; c++) {
        const speed = baseSpeed + c * 0.02;
        const frac = ((c * 0.41) + timeSec * (speed / 18.0)) % 1;
        const length = lenFn(c);
        const thickness = Math.min(Math.min(w, h) * thickFn(c), 14.0);
        const ox = -length + frac * (w + 2 * length);
        const oy = oyFn(c, frac);
        // Layered strokes to mimic feathered ribbon
        const alpha = alphaBase;
        ctx.lineCap = 'round';
        ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.45})`;
        ctx.lineWidth = thickness; ctx.beginPath(); ctx.moveTo(ox, oy); ctx.lineTo(ox + length, oy); ctx.stroke();
        ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.75})`;
        ctx.lineWidth = thickness * 0.82; ctx.beginPath(); ctx.moveTo(ox, oy); ctx.lineTo(ox + length, oy); ctx.stroke();
        ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
        ctx.lineWidth = thickness * 0.60; ctx.beginPath(); ctx.moveTo(ox, oy); ctx.lineTo(ox + length, oy); ctx.stroke();
      }
    };

    // Main set
    drawRibbonSet(
      6,
      0.18,
      (c, frac) => h * (0.10 + c * 0.055) + Math.sin(2 * Math.PI * (frac + c * 0.17)) * (h * 0.005),
      c => (0.006 + c * 0.0018),
      c => w * (0.08 + pr(31000 + c) * 0.12),
      1000,
      0.13
    );
    // Top set
    drawRibbonSet(
      3,
      0.12,
      (i) => h * (0.08 + i * 0.035),
      i => (0.0045 + i * 0.0015),
      i => w * (0.07 + pr(32000 + i) * 0.11),
      2000,
      0.12
    );
  }

  // Ground & single hill ----------------------------------------------------
  function drawGround() {
    const horizonY = h * 0.48;
    const gg = ctx.createLinearGradient(0, horizonY, 0, h);
    gg.addColorStop(0, 'rgb(207,249,199)'); // 0.81,0.97,0.78 approx
    gg.addColorStop(0.5, 'rgb(144,223,143)'); // 0.56,0.87,0.56 approx
    gg.addColorStop(1, 'rgb(77,187,107)');   // 0.30,0.73,0.42 approx
    ctx.fillStyle = gg;
    ctx.fillRect(0, horizonY, w, h - horizonY);

    // Single lowered hill (mirror of Swift hill1 but slightly lower)
    const cx = -w * 0.15 + (w * 1.3) * 0.5;
    const cy = horizonY - h * 0.12 + (h * 0.5) * 0.5; // lowered from 0.18 to 0.12
    const rx = (w * 1.3) * 0.5;
    const ry = (h * 0.5) * 0.5;
    ctx.fillStyle = 'rgb(156,234,166)';
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();

    // expose hill geometry for flowers exclusion
    return { horizonY, cx, cy, rx, ry };
  }

  // Flowers -----------------------------------------------------------------
  const flowers = { near: [], far: [] };
  function seedFlowers() {
    flowers.near = []; flowers.far = [];
    const count = 100; // main flowers
    for (let i = 0; i < count; i++) {
      const k = (i * 37 + 17) % count;
      let xr = halton(k + 1, 2) + pr(91000 + k) * 0.12;
      let yr = halton(k + 1, 3) + pr(92000 + k) * 0.12;
      xr = clamp(xr, 0, 1); yr = clamp(yr, 0, 1);
      const edgeBias = 0.28;
      const xrBiased = clamp((1 - edgeBias) * xr + edgeBias * (xr < 0.5 ? xr * 0.7 : 0.3 + (xr - 0.5) * 0.7), 0, 1);
      const yrBiased = Math.sqrt(yr);
      flowers.near.push({ id: i, xr: xrBiased, yr: yrBiased });
    }
    const farCount = 60; // far flowers)
    for (let i = 0; i < farCount; i++) {
      const k = (i * 41 + 11) % farCount;
      let xr = halton(k + 1, 2) + pr(93000 + k) * 0.10;
      let yr = halton(k + 1, 5) + pr(94000 + k) * 0.10;
      xr = clamp(xr, 0, 1); yr = clamp(yr, 0, 1);
      const edgeBias = 0.22;
      const xrBiased = clamp((1 - edgeBias) * xr + edgeBias * (xr < 0.5 ? xr * 0.7 : 0.3 + (xr - 0.5) * 0.7), 0, 1);
      const yrFar = clamp(yr * yr, 0, 1) * 0.45;
      flowers.far.push({ id: 10000 + i, xr: xrBiased, yr: yrFar });
    }
  }
  seedFlowers();

  // Grass -----------------------------------------------------------------
  const grass = { near: [], far: [] };
  function seedGrass() {
    grass.near = []; grass.far = [];
    const nearCount = 900; // subtle amount of near grass
    for (let i = 0; i < nearCount; i++) {
      const k = (i * 29 + 7) % nearCount;
      let xr = halton(k + 1, 2) + (pr(95000 + k) - 0.5) * 0.20;
      let yr = halton(k + 1, 3) + (pr(96000 + k) - 0.5) * 0.20;
      // wrap into [0,1) to avoid piling up at the edges (no clamping to 1.0)
      xr = ((xr % 1) + 1) % 1; yr = ((yr % 1) + 1) % 1;
      const edgeBias = 0.20;
      const xrBiased = clamp((1 - edgeBias) * xr + edgeBias * (xr < 0.5 ? xr * 0.7 : 0.3 + (xr - 0.5) * 0.7), 0, 1);
      const yrBiased = Math.sqrt(yr); // bias toward bottom
      grass.near.push({ id: i, xr: xrBiased, yr: yrBiased });
    }
  }
  seedGrass();

  function drawGrass(hill, timeSec) {
    // simple ellipse test to avoid drawing on hill
    const inEllipse = (x, y, cx, cy, rx, ry) => {
      if (rx <= 0 || ry <= 0) return false;
      const nx = (x - cx) / rx, ny = (y - cy) / ry;
      return nx * nx + ny * ny <= 1;
    };

    const t = timeSec;
    const horizonY = hill.horizonY;

    const drawSet = (arr, alphaBase) => {
      for (const g of arr) {
        const x = (0.015 + 0.97 * g.xr) * w;
        const groundY = (horizonY + 4) + g.yr * (h - (horizonY + 10));
        if (inEllipse(x, groundY, hill.cx, hill.cy, hill.rx, hill.ry)) continue;

        const scale = clamp((groundY - horizonY) / (h - horizonY), 0, 1);
        const bladeH = (10 + pr(60000 + g.id) * 18) * (0.25 + 0.55 * scale);
        const swayAmp = (1.5 + pr(61000 + g.id) * 2.5) * (0.5 + 0.6 * scale);
        const baseOmega = 1.0 + pr(62000 + g.id) * 0.8;
        const phase0 = pr(63000 + g.id) * 2 * Math.PI;
        const sway = Math.sin(t * baseOmega + phase0) * swayAmp;

        const tip = { x: x + sway, y: groundY - bladeH };
        const ctrl = { x: x + sway * 0.55, y: groundY - bladeH * 0.55 };

        ctx.strokeStyle = `rgba(84,184,106,${alphaBase})`;
        ctx.lineWidth = 0.9 + scale * 0.6;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(x, groundY);
        ctx.quadraticCurveTo(ctrl.x, ctrl.y, tip.x, tip.y);
        ctx.stroke();
      }
    };

    // subtle layering: far first, then near
    drawSet(grass.far, 0.65);
    drawSet(grass.near, 0.85);
  }


  function drawFlowers(hill, timeSec) {
    // helper
    const inEllipse = (x, y, cx, cy, rx, ry) => {
      if (rx <= 0 || ry <= 0) return false;
      const nx = (x - cx) / rx, ny = (y - cy) / ry;
      return nx * nx + ny * ny <= 1;
    };

    const t = timeSec;
    const all = [];
    const horizonY = hill.horizonY;

    for (const f of flowers.near) {
      const x = (0.02 + 0.96 * f.xr) * w;
      const groundY = (horizonY + 6) + f.yr * (h - (horizonY + 12));
      if (!inEllipse(x, groundY, hill.cx, hill.cy, hill.rx, hill.ry)) all.push({ id: f.id, x, groundY });
    }
    for (const f of flowers.far) {
      const x = (0.02 + 0.96 * f.xr) * w;
      const groundY = (horizonY + 6) + f.yr * (h - (horizonY + 12));
      if (!inEllipse(x, groundY, hill.cx, hill.cy, hill.rx, hill.ry)) all.push({ id: f.id, x, groundY });
    }
    all.sort((a, b) => a.groundY - b.groundY);

    const stemBase = 'rgba(46,139,87,0.95)'; // approx

    for (const item of all) {
      const scale = clamp((item.groundY - horizonY) / (h - horizonY), 0.2, 1.2);
      const sizeVar = 0.9 + 0.2 * Math.sin((item.x / w) * 2 * Math.PI + 0.8);
      const stemH = (60 + pr(3000 + item.id) * 80) * (0.28 + 0.38 * scale) * sizeVar;
      const swayAmp = (6 + pr(4000 + item.id) * 10) * (0.6 + 0.6 * scale);
      const baseOmega = 0.8 + pr(5100 + item.id) * 0.8;
      const phase0 = pr(5200 + item.id) * 2 * Math.PI;
      const sway = Math.sin(t * baseOmega + phase0) * swayAmp;

      const tip = { x: item.x + sway, y: item.groundY - stemH };
      const ctrl = { x: item.x + sway * 0.6, y: item.groundY - stemH * 0.55 };

      // stem
      ctx.strokeStyle = stemBase;
      ctx.lineWidth = 1.6 + scale * 1.2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(item.x, item.groundY);
      ctx.quadraticCurveTo(ctrl.x, ctrl.y, tip.x, tip.y);
      ctx.stroke();

      // leaves (simple ellipses rotated ~ tangent)
      const leafW = 6 + 6 * scale;
      const leafH = 4 + 4 * scale;
      function drawLeaf(tt, side) {
        const u = 1 - tt;
        const bx = u * u * item.x + 2 * u * tt * ctrl.x + tt * tt * tip.x;
        const by = u * u * item.groundY + 2 * u * tt * ctrl.y + tt * tt * tip.y;
        const dx = 2 * u * (ctrl.x - item.x) + 2 * tt * (tip.x - ctrl.x);
        const dy = 2 * u * (ctrl.y - item.groundY) + 2 * tt * (tip.y - ctrl.y);
        const len = Math.max(Math.hypot(dx, dy), 0.001);
        const nx = -dy / len, ny = dx / len;
        const cx = bx + nx * (2 + 3 * scale) * side;
        const cy = by + ny * (2 + 3 * scale) * side;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(Math.atan2(dy, dx));
        ctx.fillStyle = 'rgb(84,184,106)';
        ctx.beginPath();
        ctx.ellipse(0, 0, leafW / 2, leafH / 2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      drawLeaf(0.45, -1);
      drawLeaf(0.72, +1);

      // petals + head
      const petals = 6 + (item.id % 2);
      const headR = (4 + 4 * scale) * 0.55;
      const petalR = headR * (0.8 + 0.15 * pr(7000 + item.id));
      const col = pastelFlowerColor(item.id);
      for (let p = 0; p < petals; p++) {
        const ang = (2 * Math.PI * p / petals) + 0.15 * (sway / (swayAmp + 1e-3));
        const px = tip.x + Math.cos(ang) * (headR + petalR);
        const py = tip.y + Math.sin(ang) * (headR + petalR);
        ctx.fillStyle = rgbToStr(col, 0.95);
        ctx.beginPath();
        ctx.ellipse(px, py, petalR, petalR, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = 'rgb(255,213,79)';
      ctx.beginPath();
      ctx.ellipse(tip.x, tip.y, headR, headR, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Animation loop ----------------------------------------------------------
  const t0 = performance.now();
  function frame(now) {
    const timeSec = (now - t0) / 1000; // seconds since start

    drawSky();
    drawLaneClouds(timeSec);
    drawCirrus(timeSec);
    const hill = drawGround();
    drawGrass(hill, timeSec);
    drawFlowers(hill, timeSec);

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);
})();

